import React, { useRef, useState, useMemo, useContext } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import DataGrid from "../../components/DataGrid/index.js";
import AlertComponent from "../../components/AlertComponent";
import { Context } from "../../Reducers/Store";

export default function BackupList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const gridRef = useRef();
  const [state] = useContext(Context);

  const filtroInicial = searchParams.get("filtro") || "";
  const [periodoFiltro, setPeriodoFiltro] = useState(searchParams.get("periodo") || "");
  const [dataInicio, setDataInicio] = useState(searchParams.get("dataInicio") || "");
  const [dataFim, setDataFim] = useState(searchParams.get("dataFim") || "");
  const [mesSelecionado, setMesSelecionado] = useState(searchParams.get("mes") || "");
  const [anoSelecionado, setAnoSelecionado] = useState(searchParams.get("ano") || new Date().getFullYear().toString());
  const [downloading, setDownloading] = useState(null);

  // Gera lista de anos (últimos 5 anos)
  const anos = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);
  
  const meses = [
    { value: "1", label: "Janeiro" },
    { value: "2", label: "Fevereiro" },
    { value: "3", label: "Março" },
    { value: "4", label: "Abril" },
    { value: "5", label: "Maio" },
    { value: "6", label: "Junho" },
    { value: "7", label: "Julho" },
    { value: "8", label: "Agosto" },
    { value: "9", label: "Setembro" },
    { value: "10", label: "Outubro" },
    { value: "11", label: "Novembro" },
    { value: "12", label: "Dezembro" },
  ];

  // Verifica permissão de download
  const podeDownload = state?.profile?.permissoes?.includes("backups.download") ?? false;

  const handlePeriodoChange = (valor) => {
    setPeriodoFiltro(valor);
    const params = new URLSearchParams(searchParams);
    
    if (valor !== "") {
      params.set("periodo", valor);
    } else {
      params.delete("periodo");
    }

    // Limpa campos que não são do período selecionado
    if (valor !== "data" && valor !== "entre_datas") {
      params.delete("dataInicio");
      params.delete("dataFim");
      setDataInicio("");
      setDataFim("");
    }

    if (valor !== "mensal") {
      params.delete("mes");
      params.delete("ano");
      setMesSelecionado("");
    }

    setSearchParams(params);
  };

  const handleMesChange = (valor) => {
    setMesSelecionado(valor);
    const params = new URLSearchParams(searchParams);
    if (valor !== "") {
      params.set("mes", valor);
    } else {
      params.delete("mes");
    }
    setSearchParams(params);
  };

  const handleAnoChange = (valor) => {
    setAnoSelecionado(valor);
    const params = new URLSearchParams(searchParams);
    if (valor !== "") {
      params.set("ano", valor);
    } else {
      params.delete("ano");
    }
    setSearchParams(params);
  };

  const handleDataInicioChange = (valor) => {
    setDataInicio(valor);
    const params = new URLSearchParams(searchParams);
    if (valor !== "") {
      params.set("dataInicio", valor);
    } else {
      params.delete("dataInicio");
    }
    setSearchParams(params);
  };

  const handleDataFimChange = (valor) => {
    setDataFim(valor);
    const params = new URLSearchParams(searchParams);
    if (valor !== "") {
      params.set("dataFim", valor);
    } else {
      params.delete("dataFim");
    }
    setSearchParams(params);
  };

  const handleDownload = async (key, nome) => {
    if (!podeDownload) {
      AlertComponent("Erro", "Você não tem permissão para fazer download.", { icon: "error" });
      return;
    }

    setDownloading(key);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API}/backups/download?key=${encodeURIComponent(key)}`
      );

      if (response.data.success && response.data.data?.url) {
        // Abre o link de download em nova aba
        window.open(response.data.data.url, "_blank");
      } else {
        AlertComponent("Erro", response.data.message || "Erro ao gerar link de download", { icon: "error" });
      }
    } catch (error) {
      console.error("Erro ao fazer download:", error);
      AlertComponent("Erro", error?.response?.data?.message ?? "Erro ao gerar link de download", { icon: "error" });
    } finally {
      setDownloading(null);
    }
  };

  const formatarData = (data) => {
    if (!data) return "-";
    return new Date(data).toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getColumns = () => {
    const columns = [
      { field: "nome", header: "Nome", sortable: true },
      {
        key: "data",
        header: "Data",
        width: "180px",
        render: (row) => formatarData(row.data),
      },
      {
        key: "tamanho",
        header: "Tamanho",
        width: "120px",
        className: "text-right",
        headerClassName: "text-right",
        render: (row) => row.tamanhoFormatado || "-",
      },
    ];

    // Coluna de ações só se tiver permissão de download
    if (podeDownload) {
      columns.push({
        key: "acoes",
        header: "Ações",
        width: "120px",
        className: "text-center",
        headerClassName: "text-center",
        render: (row) => (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleDownload(row.key, row.nome);
            }}
            disabled={downloading === row.key}
            className="px-3 py-1.5 text-sm rounded bg-slate-800 text-white hover:bg-slate-700 disabled:opacity-50"
          >
            {downloading === row.key ? "..." : "Download"}
          </button>
        ),
      });
    }

    return columns;
  };

  const externalFilters = useMemo(() => {
    const filters = {};
    if (periodoFiltro !== "") filters.periodo = periodoFiltro;
    if (dataInicio !== "") filters.dataInicio = dataInicio;
    if (dataFim !== "") filters.dataFim = dataFim;
    if (mesSelecionado !== "") filters.mes = mesSelecionado;
    if (anoSelecionado !== "") filters.ano = anoSelecionado;
    return filters;
  }, [periodoFiltro, dataInicio, dataFim, mesSelecionado, anoSelecionado]);

  const limparFiltros = () => {
    setPeriodoFiltro("");
    setDataInicio("");
    setDataFim("");
    setMesSelecionado("");
    setAnoSelecionado(new Date().getFullYear().toString());
    setSearchParams({});
  };

  const temFiltrosAtivos = periodoFiltro || dataInicio || dataFim || mesSelecionado;

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <div>
          <h1 className="text-xl font-bold">Backups</h1>
          <p className="text-sm text-gray-500">Arquivos de backup disponíveis para download</p>
          {temFiltrosAtivos && (
            <button
              onClick={limparFiltros}
              className="text-sm text-blue-600 hover:underline"
            >
              Limpar filtros
            </button>
          )}
        </div>

        <div className="flex items-center gap-4">
          {/* Filtro de Período */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">Período:</span>
            <select
              value={periodoFiltro}
              onChange={(e) => handlePeriodoChange(e.target.value)}
              className="border rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-slate-300"
            >
              <option value="">Todos</option>
              <option value="diario">Hoje</option>
              <option value="mensal">Mensal</option>
              <option value="data">A partir de</option>
              <option value="entre_datas">Entre datas</option>
            </select>
          </div>

          {/* Mês e Ano - aparece quando período = "mensal" */}
          {periodoFiltro === "mensal" && (
            <>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Mês:</span>
                <select
                  value={mesSelecionado}
                  onChange={(e) => handleMesChange(e.target.value)}
                  className="border rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-slate-300"
                >
                  <option value="">Todos</option>
                  {meses.map((m) => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Ano:</span>
                <select
                  value={anoSelecionado}
                  onChange={(e) => handleAnoChange(e.target.value)}
                  className="border rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-slate-300"
                >
                  {anos.map((a) => (
                    <option key={a} value={a}>{a}</option>
                  ))}
                </select>
              </div>
            </>
          )}

          {/* Data Início */}
          {(periodoFiltro === "data" || periodoFiltro === "entre_datas") && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">
                {periodoFiltro === "entre_datas" ? "De:" : "Data:"}
              </span>
              <input
                type="date"
                value={dataInicio}
                onChange={(e) => handleDataInicioChange(e.target.value)}
                className="border rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-slate-300"
              />
            </div>
          )}

          {/* Data Fim */}
          {periodoFiltro === "entre_datas" && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Até:</span>
              <input
                type="date"
                value={dataFim}
                onChange={(e) => handleDataFimChange(e.target.value)}
                className="border rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-slate-300"
              />
            </div>
          )}
        </div>
      </div>

      <DataGrid
        ref={gridRef}
        endpoint={`${process.env.REACT_APP_API}/backups`}
        columns={getColumns()}
        filters={externalFilters}
        initialFilter={filtroInicial}
        emptyMessage="Nenhum backup encontrado"
        showStatusFilter={false}
      />
    </div>
  );
}